import { z } from 'zod';
import { normalizeComparableUom } from './uomConvert.js';

export const STOCK_COMMAND_VERSION = 1 as const;

export const StockMovementClassSchema = z.enum([
  'receive',
  'inventory_adjustment',
  'lot_state',
  'correction',
  'consume',
  'dispatch',
  'uom_rebase',
  'transfer',
  'production',
  'reverse',
]);
export type StockMovementClass = z.infer<typeof StockMovementClassSchema>;

export const StockEconomicEffectSchema = z.enum([
  'purchase',
  'stock_only',
  'internal',
  'reversal',
]);
export type StockEconomicEffect = z.infer<typeof StockEconomicEffectSchema>;

export const StockConversionSourceSchema = z.enum([
  'identity',
  'metric',
  'document_pack',
  'document_weight',
  'supplier_alias',
  'user_confirmed',
  'carried',
]);
export type StockConversionSource = z.infer<typeof StockConversionSourceSchema>;

// PostgreSQL jsonb renders numbers without JavaScript's exponent notation.
// Keeping command-domain numbers inside JSON.stringify's plain-decimal range
// makes the client and DB canonical digest byte-identical. Zero remains valid
// where the domain explicitly permits it.
const MIN_CANONICAL_NONZERO_NUMBER = 0.000001;
const MAX_CANONICAL_NUMBER = 1e21;
const CanonicalFiniteNumberSchema = z.number().finite().refine((value) => (
  value === 0
  || (
    Math.abs(value) >= MIN_CANONICAL_NONZERO_NUMBER
    && Math.abs(value) < MAX_CANONICAL_NUMBER
  )
), {
  message: 'Numero fuori dall’intervallo canonico JSON del comando',
});
const PositiveFiniteSchema = CanonicalFiniteNumberSchema.pipe(z.number().positive());
const NonNegativeFiniteSchema =
  CanonicalFiniteNumberSchema.pipe(z.number().nonnegative());
// No transform: source_uom is an immutable audit value. Normalization is used
// only for validation/comparison; the materialized payload keeps the input.
const UomSchema = z.string().min(1).max(10).refine((value) => (
  value.length > 0 && value === value.trim()
), {
  message: 'UdM vuota o con spazi esterni',
});
const CurrencySchema = z.string().regex(/^[A-Z]{3}$/);
const SAME_UOM_MULTIPLIER_SOURCES: ReadonlySet<StockConversionSource> = new Set([
  'document_pack',
  'supplier_alias',
  'user_confirmed',
]);

const LocatorShape = {
  food_id: z.string().uuid(),
  batch_lot: z.string().max(100).nullable().optional(),
  expiration_date: z.string().date().nullable().optional(),
  supplier_id: z.string().uuid().nullable().optional(),
  document_id: z.string().uuid().nullable().optional(),
  storage_unit_id: z.string().uuid().nullable().optional(),
};

const StockLineMetadataShape = {
  operator_name: z.string().trim().min(1).max(200).nullable().optional(),
  supplier_sku: z.string().trim().min(1).max(200).nullable().optional(),
  document_number: z.string().trim().min(1).max(200).nullable().optional(),
  document_date: z.string().date().nullable().optional(),
  photo_path: z.string().trim().min(1).max(2_048).nullable().optional(),
  notify_expiration: z.boolean().optional(),
  is_frozen: z.boolean().optional(),
  manual_entry: z.boolean().optional(),
  // Solo per il carico manuale: attestazione esplicita dell'operatore prima
  // che la merce diventi una riga del registro di ricevimento.
  receiving_conformity_confirmed: z.boolean().optional(),
  pack_factor_applied: z.boolean().optional(),
  keep_current_lot: z.boolean().optional(),
};

export const StockConversionV1Schema = z.object({
  factor: PositiveFiniteSchema,
  source: StockConversionSourceSchema,
  reference_id: z.string().min(1).max(200).nullable().optional(),
});
export type StockConversionV1 = z.infer<typeof StockConversionV1Schema>;

const MASS_IN_KG: Readonly<Record<string, number>> = { kg: 1, g: 0.001 };
const VOLUME_IN_LITRES: Readonly<Record<string, number>> = { l: 1, ml: 0.001 };

const normalizedUom = normalizeComparableUom;

function nearlyEqual(left: number, right: number): boolean {
  const scale = Math.max(1, Math.abs(left), Math.abs(right));
  // Keep the client contract aligned with the PostgreSQL validator. Source
  // quantities often come from decimal division (packs/weights), so machine
  // epsilon is unnecessarily strict and can reject an otherwise identical
  // command before the server sees it.
  return Math.abs(left - right) <= 1e-12 * scale;
}

function metricFactor(from: string, to: string): number | null {
  const fromMass = MASS_IN_KG[from];
  const toMass = MASS_IN_KG[to];
  if (fromMass != null && toMass != null) return fromMass / toMass;

  const fromVolume = VOLUME_IN_LITRES[from];
  const toVolume = VOLUME_IN_LITRES[to];
  if (fromVolume != null && toVolume != null) return fromVolume / toVolume;

  return null;
}

function isMassVolumeMismatch(from: string, to: string): boolean {
  return (
    (MASS_IN_KG[from] != null && VOLUME_IN_LITRES[to] != null)
    || (VOLUME_IN_LITRES[from] != null && MASS_IN_KG[to] != null)
  );
}

interface ConversionCandidate {
  source_qty: number;
  source_uom: string;
  stock_uom: string;
  conversion: StockConversionV1;
}

function validateConversion(
  value: ConversionCandidate,
  ctx: z.RefinementCtx,
  stockQty?: number,
): void {
  const from = normalizedUom(value.source_uom);
  const to = normalizedUom(value.stock_uom);
  const { factor, source } = value.conversion;

  if (!Number.isFinite(value.source_qty * factor)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['conversion', 'factor'],
      message: 'La quantità stock derivata non è finita',
    });
    return;
  }

  if (from === to) {
    if (source === 'metric') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['conversion', 'source'],
        message: 'La conversione metric non è ammessa tra UdM uguali',
      });
    } else if (source === 'identity' && !nearlyEqual(factor, 1)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['conversion', 'factor'],
        message: 'La conversione identity richiede fattore 1',
      });
    } else if (!nearlyEqual(factor, 1) && !SAME_UOM_MULTIPLIER_SOURCES.has(source)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['conversion', 'source'],
        message: 'Il moltiplicatore tra UdM uguali richiede pack, alias fornitore o conferma utente',
      });
    }
  } else {
    const expectedMetricFactor = metricFactor(from, to);
    if (expectedMetricFactor != null) {
      if (source !== 'metric' || !nearlyEqual(factor, expectedMetricFactor)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['conversion'],
          message: `Conversione metrica ${from}→${to} non coerente`,
        });
      }
    } else if (isMassVolumeMismatch(from, to)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['conversion'],
        message: `UdM incompatibili: ${from}→${to}`,
      });
    } else if (source === 'identity' || source === 'metric') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['conversion', 'source'],
        message: `La conversione ${from}→${to} richiede un fattore esplicito`,
      });
    }
  }

  if (stockQty != null && !nearlyEqual(value.source_qty * factor, stockQty)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['stock_qty'],
      message: 'stock_qty non conserva la conversione source_qty × factor',
    });
  }
}

export const StockSourceLineV1Schema = z.object({
  ...LocatorShape,
  ...StockLineMetadataShape,
  line_ref: z.string().min(1).max(200).nullable().optional(),
  lot_action: z.enum(['updated_active', 'recorded_only']).optional(),
  purchase_cost_source: z.enum(['document', 'manual']).optional(),
  source_qty: PositiveFiniteSchema,
  source_uom: UomSchema,
  source_unit_price: NonNegativeFiniteSchema.nullable(),
  source_line_total: NonNegativeFiniteSchema.nullable(),
  currency: CurrencySchema,
  stock_uom: UomSchema,
  conversion: StockConversionV1Schema,
}).superRefine((line, ctx) => validateConversion(line, ctx));
export type StockSourceLineV1 = z.infer<typeof StockSourceLineV1Schema>;

/**
 * A tenant-wide UoM cutover still needs an append-only boundary when the
 * authoritative balance is zero. This marker carries no stock/value and is
 * deliberately distinguishable from a physical stock line.
 */
export const ZeroStockUomRebaseLineV1Schema = z.object({
  food_id: z.string().uuid(),
  batch_lot: z.null().optional(),
  expiration_date: z.null().optional(),
  supplier_id: z.null().optional(),
  document_id: z.null().optional(),
  storage_unit_id: z.null().optional(),
  line_ref: z.string().min(1).max(200).nullable().optional(),
  source_qty: z.literal(0),
  source_uom: UomSchema,
  source_unit_price: z.null(),
  source_line_total: z.null(),
  currency: CurrencySchema,
  stock_uom: UomSchema,
  conversion: StockConversionV1Schema,
  zero_balance: z.literal(true),
}).superRefine((line, ctx) => validateConversion(line, ctx, 0));
export type ZeroStockUomRebaseLineV1 =
  z.infer<typeof ZeroStockUomRebaseLineV1Schema>;

/**
 * Consumption may atomically exhaust and close one lot. The explicit literal
 * avoids inferring closure from a reason string or from a client-side balance:
 * PostgreSQL still verifies that this line consumes the full authoritative
 * on-hand quantity with no event/draft reservation.
 */
export const ConsumeStockLineV1Schema = StockSourceLineV1Schema.and(z.object({
  close_lot: z.literal(true).optional(),
})).superRefine((line, ctx) => {
  if (
    line.close_lot
    && (typeof line.batch_lot !== 'string' || line.batch_lot.trim().length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['batch_lot'],
      message: 'La chiusura atomica richiede un lotto identificato',
    });
  }
});
export type ConsumeStockLineV1 = z.infer<typeof ConsumeStockLineV1Schema>;

export const InventoryCountLineV1Schema = z.object({
  ...LocatorShape,
  ...StockLineMetadataShape,
  line_ref: z.string().min(1).max(200).nullable().optional(),
  system_qty: NonNegativeFiniteSchema,
  counted_qty: NonNegativeFiniteSchema,
  uom: UomSchema,
  unit_cost: NonNegativeFiniteSchema.nullable(),
  currency: CurrencySchema,
});
export type InventoryCountLineV1 = z.infer<typeof InventoryCountLineV1Schema>;

export const LotStateLineV1Schema = z.object({
  ...LocatorShape,
  ...StockLineMetadataShape,
  batch_lot: z.string().trim().min(1).max(100).nullable(),
  line_ref: z.string().min(1).max(200).nullable().optional(),
  source_production_id: z.string().uuid().nullable().optional(),
  action: z.enum(['register', 'close', 'update_metadata']),
  uom: UomSchema,
}).superRefine((line, ctx) => {
  if (line.action !== 'update_metadata' && line.batch_lot === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['batch_lot'],
      message: 'Registrazione e chiusura richiedono un lotto identificato',
    });
  }
});
export type LotStateLineV1 = z.infer<typeof LotStateLineV1Schema>;

export const StockBalanceExpectationV1Schema = z.object({
  qty: NonNegativeFiniteSchema,
  reserved_qty: NonNegativeFiniteSchema,
  uom: UomSchema,
  event_count: z.number().int().positive(),
  last_event_ref: z.string().uuid(),
  last_server_rev: z.number().int().positive(),
});
export type StockBalanceExpectationV1 = z.infer<typeof StockBalanceExpectationV1Schema>;

export const StockCorrectionSourceV1Schema = z.object({
  food_id: z.string().uuid(),
  business_id: z.string().uuid(),
  batch_lot: z.string().trim().min(1).max(100).nullable(),
  expiration_date: z.string().date().nullable(),
  supplier_id: z.string().uuid().nullable(),
  document_id: z.string().uuid(),
  storage_unit_id: z.string().uuid().nullable(),
  qty: PositiveFiniteSchema,
  uom: UomSchema,
  unit_cost: NonNegativeFiniteSchema.nullable(),
  currency: CurrencySchema,
  original_event_id: z.string().uuid(),
  original_command_id: z.string().uuid().nullable(),
  original_command_seq: z.number().int().positive().nullable(),
  correction_root_event_id: z.string().uuid(),
  expected_balance: StockBalanceExpectationV1Schema,
}).superRefine((source, ctx) => {
  if (
    (source.original_command_id === null)
    !== (source.original_command_seq === null)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['original_command_id'],
      message: 'ID e sequenza del comando originale devono essere entrambi presenti o assenti',
    });
  }
  if (normalizedUom(source.expected_balance.uom) !== normalizedUom(source.uom)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['expected_balance', 'uom'],
      message: 'Il saldo atteso deve usare la UdM della coordinata sorgente',
    });
  }
  if (
    source.expected_balance.qty < source.qty
    && !nearlyEqual(source.expected_balance.qty, source.qty)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['expected_balance', 'qty'],
      message: 'Il saldo atteso non copre la quantità da correggere',
    });
  }
  if (source.expected_balance.reserved_qty !== 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['expected_balance', 'reserved_qty'],
      message: 'La correzione richiede che la coordinata non abbia quantità riservate',
    });
  }
});
export type StockCorrectionSourceV1 = z.infer<typeof StockCorrectionSourceV1Schema>;

export const StockCorrectionTargetV1Schema = z.object({
  batch_lot: z.string().trim().min(1).max(100).nullable(),
  expiration_date: z.string().date().nullable(),
  qty: PositiveFiniteSchema,
  uom: UomSchema,
});
export type StockCorrectionTargetV1 = z.infer<typeof StockCorrectionTargetV1Schema>;

export const StockCorrectionLineV1Schema = z.object({
  line_ref: z.string().min(1).max(200).nullable().optional(),
  operator_name: z.string().trim().min(1).max(200),
  document_number: z.string().trim().min(1).max(200).nullable(),
  source: StockCorrectionSourceV1Schema,
  target: StockCorrectionTargetV1Schema,
  conversion: StockConversionV1Schema,
}).superRefine((line, ctx) => {
  validateConversion({
    source_qty: line.source.qty,
    source_uom: line.source.uom,
    stock_uom: line.target.uom,
    conversion: line.conversion,
  }, ctx, line.target.qty);

  if (!(['identity', 'metric', 'user_confirmed'] as const).includes(
    line.conversion.source as 'identity' | 'metric' | 'user_confirmed',
  )) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['conversion', 'source'],
      message: 'La correzione richiede una conversione identity, metric o confermata dall’utente',
    });
  }
  if (
    line.source.qty === line.target.qty
    && normalizedUom(line.source.uom) === normalizedUom(line.target.uom)
    && line.source.batch_lot === line.target.batch_lot
    && line.source.expiration_date === line.target.expiration_date
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['target'],
      message: 'La correzione non modifica quantità, UdM, lotto o scadenza',
    });
  }
});
export type StockCorrectionLineV1 = z.infer<typeof StockCorrectionLineV1Schema>;

export const ReversibleStockMovementV1Schema = z.object({
  ...LocatorShape,
  line_ref: z.string().min(1).max(200).nullable().optional(),
  // DB row identity and client/logical identity are deliberately separate:
  // events_local.id is client_uuid, while receipt writeback.id is the server
  // row UUID. Reverse commands must carry both so neither side guesses.
  original_event_id: z.string().uuid(),
  original_event_client_uuid: z.string().uuid(),
  original_seq: z.number().int().positive(),
  kind: z.enum(['stock_in', 'stock_out']),
  business_id: z.string().uuid(),
  source_qty: PositiveFiniteSchema,
  source_uom: UomSchema,
  source_unit_price: NonNegativeFiniteSchema.nullable(),
  source_line_total: NonNegativeFiniteSchema.nullable(),
  currency: CurrencySchema,
  stock_qty: PositiveFiniteSchema,
  stock_uom: UomSchema,
  unit_cost: NonNegativeFiniteSchema.nullable(),
  conversion: StockConversionV1Schema,
}).superRefine((movement, ctx) => {
  validateConversion(movement, ctx, movement.stock_qty);
  const lineValue = sourceLineValue(movement);
  if (
    lineValue != null
    && movement.unit_cost != null
    && !nearlyEqual(movement.unit_cost * movement.stock_qty, lineValue)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['unit_cost'],
      message: 'unit_cost non conserva il valore sorgente del movimento',
    });
  }
});
export type ReversibleStockMovementV1 = z.infer<typeof ReversibleStockMovementV1Schema>;

const CommonCommandShape = {
  version: z.literal(STOCK_COMMAND_VERSION),
  command_id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  reason: z.string().trim().min(1).max(500).nullable().optional(),
};

const ReceiveStockCommandV1Schema = z.object({
  ...CommonCommandShape,
  movement_class: z.literal('receive'),
  economic_effect: z.enum(['purchase', 'stock_only']),
  business_id: z.string().uuid(),
  lines: z.array(StockSourceLineV1Schema).min(1).max(1_000),
});

const InventoryAdjustmentStockCommandV1Schema = z.object({
  ...CommonCommandShape,
  movement_class: z.literal('inventory_adjustment'),
  economic_effect: z.literal('stock_only'),
  business_id: z.string().uuid(),
  counted_at: z.string().datetime(),
  lines: z.array(InventoryCountLineV1Schema).min(1).max(1_000),
});

const LotStateStockCommandV1Schema = z.object({
  ...CommonCommandShape,
  movement_class: z.literal('lot_state'),
  economic_effect: z.literal('stock_only'),
  business_id: z.string().uuid(),
  lines: z.array(LotStateLineV1Schema).min(1).max(1_000),
});

const CorrectionStockCommandV1Schema = z.object({
  ...CommonCommandShape,
  movement_class: z.literal('correction'),
  economic_effect: z.literal('stock_only'),
  reason: z.string().trim().min(1).max(500),
  // V1 corregge una singola riga: evita che due storni sullo stesso saldo
  // condividano la medesima precondizione e possano sovrascaricarlo.
  lines: z.array(StockCorrectionLineV1Schema).length(1),
});

const DispatchStockCommandV1Schema = z.object({
  ...CommonCommandShape,
  movement_class: z.literal('dispatch'),
  economic_effect: z.literal('stock_only'),
  business_id: z.string().uuid(),
  shipment_id: z.string().uuid(),
  lines: z.array(StockSourceLineV1Schema).min(1).max(1_000),
});

const ConsumeStockCommandV1Schema = z.object({
  ...CommonCommandShape,
  movement_class: z.literal('consume'),
  economic_effect: z.literal('stock_only'),
  reason: z.string().trim().min(1).max(500),
  business_id: z.string().uuid(),
  lines: z.array(ConsumeStockLineV1Schema).min(1).max(1_000),
});

const UomRebaseStockCommandV1Schema = z.object({
  ...CommonCommandShape,
  movement_class: z.literal('uom_rebase'),
  economic_effect: z.literal('stock_only'),
  business_id: z.string().uuid(),
  lines: z.array(z.union([
    StockSourceLineV1Schema,
    ZeroStockUomRebaseLineV1Schema,
  ])).min(1).max(1_000),
});

const TransferStockCommandV1Schema = z.object({
  ...CommonCommandShape,
  movement_class: z.literal('transfer'),
  economic_effect: z.literal('internal'),
  from_business_id: z.string().uuid(),
  to_business_id: z.string().uuid(),
  shipment_id: z.string().uuid().nullable().optional(),
  lines: z.array(StockSourceLineV1Schema).min(1).max(1_000),
});

const ProductionStockCommandV1Schema = z.object({
  ...CommonCommandShape,
  movement_class: z.literal('production'),
  economic_effect: z.literal('internal'),
  business_id: z.string().uuid(),
  production_id: z.string().uuid().nullable().optional(),
  inputs: z.array(StockSourceLineV1Schema).max(1_000),
  outputs: z.array(StockSourceLineV1Schema).max(1_000),
});

const ReverseStockCommandV1Schema = z.object({
  ...CommonCommandShape,
  movement_class: z.literal('reverse'),
  economic_effect: z.literal('reversal'),
  reason: z.string().trim().min(1).max(500),
  reverses_command_id: z.string().uuid(),
  movements: z.array(ReversibleStockMovementV1Schema).min(1).max(2_000),
});

const StockCommandV1DiscriminatedSchema = z.discriminatedUnion('movement_class', [
  ReceiveStockCommandV1Schema,
  InventoryAdjustmentStockCommandV1Schema,
  LotStateStockCommandV1Schema,
  CorrectionStockCommandV1Schema,
  ConsumeStockCommandV1Schema,
  DispatchStockCommandV1Schema,
  UomRebaseStockCommandV1Schema,
  TransferStockCommandV1Schema,
  ProductionStockCommandV1Schema,
  ReverseStockCommandV1Schema,
]);

export const StockCommandV1Schema = StockCommandV1DiscriminatedSchema.superRefine((command, ctx) => {
  if (
    command.movement_class === 'transfer'
    && command.from_business_id === command.to_business_id
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['to_business_id'],
      message: 'Le sedi di origine e destinazione devono essere diverse',
    });
  }
  if (command.movement_class === 'transfer') {
    command.lines.forEach((line, index) => {
      if (
        normalizedUom(line.source_uom) !== normalizedUom(line.stock_uom)
        || line.conversion.source !== 'identity'
        || !nearlyEqual(line.conversion.factor, 1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines', index, 'conversion'],
          message: 'Il trasferimento conserva la stessa UdM stock tra le sedi',
        });
      }
    });
  }

  // Una production senza gambe quantitative è il comando di coordinamento
  // usato dai compound master-only (bozza, prestampa e ristampa). Il comando
  // di dominio, preso da solo, non può vedere gli effetti master: il relativo
  // envelope resta fail-closed e richiede almeno un master_effect.
  if (
    command.movement_class === 'production'
    && command.outputs.length > 0
    && command.production_id == null
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['production_id'],
      message: 'Una produzione con output richiede la lavorazione master',
    });
  }

  if (command.movement_class === 'uom_rebase') {
    const first = command.lines[0];
    const zeroBalanceCutover = first != null && 'zero_balance' in first;
    const seenLocators = new Set<string>();
    if (zeroBalanceCutover && command.lines.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['lines'],
        message: 'Il rebase a saldo zero richiede un solo marker globale',
      });
    }
    command.lines.forEach((line, index) => {
      if (('zero_balance' in line) !== zeroBalanceCutover) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines', index, 'zero_balance'],
          message: 'Un rebase non può mescolare marker a saldo zero e lotti fisici',
        });
      }
      if (normalizedUom(line.source_uom) === normalizedUom(line.stock_uom)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines', index, 'stock_uom'],
          message: 'Il rebase richiede una UdM di destinazione diversa',
        });
      }
      if (
        first
        && (
          line.food_id !== first.food_id
          || normalizedUom(line.source_uom) !== normalizedUom(first.source_uom)
          || normalizedUom(line.stock_uom) !== normalizedUom(first.stock_uom)
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines', index],
          message: 'Un rebase converte un solo alimento fra una coppia UdM uniforme',
        });
      }
      const locator = JSON.stringify([
        line.food_id,
        line.batch_lot?.trim() || null,
      ]);
      if (seenLocators.has(locator)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines', index, 'batch_lot'],
          message: 'Il rebase non ammette due righe per lo stesso lotto',
        });
      }
      seenLocators.add(locator);
    });
  }

  if (command.movement_class === 'correction') {
    const originalEvents = new Set<string>();
    command.lines.forEach((line, index) => {
      if (line.source.original_command_id === command.command_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines', index, 'source', 'original_command_id'],
          message: 'Una correzione non può sostituire se stessa',
        });
      }
      if (originalEvents.has(line.source.original_event_id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines', index, 'source', 'original_event_id'],
          message: 'Evento sorgente duplicato nella stessa correzione',
        });
      }
      originalEvents.add(line.source.original_event_id);
    });
  }

  if (command.movement_class === 'reverse') {
    if (command.command_id === command.reverses_command_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reverses_command_id'],
        message: 'Un comando non può annullare se stesso',
      });
    }
    const seen = new Set<number>();
    command.movements.forEach((movement, index) => {
      if (seen.has(movement.original_seq)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['movements', index, 'original_seq'],
          message: 'original_seq duplicato',
        });
      }
      seen.add(movement.original_seq);
    });
  }
});
export type StockCommandV1 = z.infer<typeof StockCommandV1Schema>;

export interface MaterializedStockPayloadV1 {
  food_id: string;
  qty: number;
  uom: string;
  batch_lot: string | null;
  expiration_date: string | null;
  supplier_id: string | null;
  document_id: string | null;
  storage_unit_id: string | null;
  unit_price: number | null;
  line_total: number | null;
  currency: string | null;
  unit_cost: number | null;
  cost_source: 'document' | 'manual' | 'carried_lot' | null;
  source_qty: number;
  source_uom: string;
  source_unit_price: number | null;
  source_line_total: number | null;
  source_currency: string;
  stock_qty: number;
  stock_uom: string;
  stock_unit_cost: number | null;
  conversion_factor: number;
  conversion_source: StockConversionSource;
  command_id: string;
  command_version: typeof STOCK_COMMAND_VERSION;
  command_seq: number;
  movement_class: StockMovementClass;
  economic_effect: StockEconomicEffect;
  [key: string]: unknown;
}

export interface MaterializedStockEventV1 {
  tenant_id: string;
  business_id: string;
  kind: 'stock_in' | 'stock_out';
  payload: MaterializedStockPayloadV1;
}

interface PayloadSource {
  food_id: string;
  batch_lot?: string | null;
  expiration_date?: string | null;
  supplier_id?: string | null;
  document_id?: string | null;
  storage_unit_id?: string | null;
  line_ref?: string | null;
  operator_name?: string | null;
  supplier_sku?: string | null;
  document_number?: string | null;
  document_date?: string | null;
  photo_path?: string | null;
  notify_expiration?: boolean;
  is_frozen?: boolean;
  manual_entry?: boolean;
  receiving_conformity_confirmed?: boolean;
  pack_factor_applied?: boolean;
  keep_current_lot?: boolean;
  purchase_cost_source?: 'document' | 'manual';
  source_qty: number;
  source_uom: string;
  source_unit_price: number | null;
  source_line_total: number | null;
  currency: string;
}

function sourceLineValue(source: {
  source_qty: number;
  source_unit_price: number | null;
  source_line_total: number | null;
}): number | null {
  if (source.source_line_total != null) return source.source_line_total;
  if (source.source_unit_price != null) return source.source_unit_price * source.source_qty;
  return null;
}

function stockLineMetadata(source: PayloadSource): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const copy = <Key extends keyof PayloadSource>(key: Key): void => {
    if (source[key] !== undefined) payload[key] = source[key];
  };
  copy('operator_name');
  copy('supplier_sku');
  copy('document_number');
  copy('document_date');
  copy('photo_path');
  copy('notify_expiration');
  copy('is_frozen');
  copy('manual_entry');
  copy('receiving_conformity_confirmed');
  copy('pack_factor_applied');
  copy('keep_current_lot');
  return payload;
}

/**
 * Validates and deterministically expands one logical command into legacy
 * stock_in/stock_out event drafts. The caller still owns the event envelope,
 * outbox transaction and DB-authored revision/hash chain.
 */
export function materializeStockCommand(input: unknown): MaterializedStockEventV1[] {
  const command = StockCommandV1Schema.parse(input);
  const events: MaterializedStockEventV1[] = [];

  const pushEvent = (
    kind: MaterializedStockEventV1['kind'],
    businessId: string,
    source: PayloadSource,
    stockQty: number,
    stockUom: string,
    conversion: StockConversionV1,
    fallbackUnitCost: number | null = null,
    extra: Readonly<Record<string, unknown>> = {},
  ): void => {
    const permitsZeroQuantity = command.movement_class === 'lot_state'
      || (
        command.movement_class === 'uom_rebase'
        && 'zero_balance' in source
        && source.zero_balance === true
      );
    if (
      !Number.isFinite(stockQty)
      || stockQty < 0
      || (stockQty === 0 && !permitsZeroQuantity)
    ) {
      throw new Error('stock_qty derivata non valida');
    }
    const lineValue = sourceLineValue(source);
    const stockUnitCost = lineValue != null ? lineValue / stockQty : fallbackUnitCost;
    if (stockUnitCost != null && (!Number.isFinite(stockUnitCost) || stockUnitCost < 0)) {
      throw new Error('unit_cost derivato non valido');
    }
    // Legacy Food Cost triggers treat every stock_in carrying unit_cost as a
    // fresh purchase observation. Project those legacy fields only for a real
    // purchase; internal, reversal and inventory legs retain their valuation
    // exclusively in the additive source_*/stock_unit_cost coordinates.
    const projectsPurchaseCost = kind === 'stock_in'
      && command.economic_effect === 'purchase';
    const legacyUnitCost = projectsPurchaseCost ? stockUnitCost : null;

    const payload: MaterializedStockPayloadV1 = {
      food_id: source.food_id,
      qty: stockQty,
      uom: stockUom,
      batch_lot: source.batch_lot ?? null,
      expiration_date: source.expiration_date ?? null,
      supplier_id: source.supplier_id ?? null,
      document_id: source.document_id ?? null,
      storage_unit_id: source.storage_unit_id ?? null,
      unit_price: projectsPurchaseCost ? source.source_unit_price : null,
      line_total: projectsPurchaseCost ? source.source_line_total : null,
      currency: legacyUnitCost != null ? source.currency : null,
      unit_cost: legacyUnitCost,
      cost_source: legacyUnitCost != null
        ? (source.purchase_cost_source ?? 'document')
        : null,
      source_qty: source.source_qty,
      source_uom: source.source_uom,
      source_unit_price: source.source_unit_price,
      source_line_total: source.source_line_total,
      source_currency: source.currency,
      stock_qty: stockQty,
      stock_uom: stockUom,
      stock_unit_cost: stockUnitCost,
      conversion_factor: conversion.factor,
      conversion_source: conversion.source,
      command_id: command.command_id,
      command_version: command.version,
      command_seq: events.length + 1,
      movement_class: command.movement_class,
      economic_effect: command.economic_effect,
      ...(source.line_ref ? { source_line_ref: source.line_ref } : {}),
      ...(conversion.reference_id ? { conversion_reference_id: conversion.reference_id } : {}),
      ...(command.reason ? { reason: command.reason } : {}),
      ...stockLineMetadata(source),
      ...extra,
    };
    events.push({
      tenant_id: command.tenant_id,
      business_id: businessId,
      kind,
      payload,
    });
  };

  if (command.movement_class === 'receive') {
    for (const line of command.lines) {
      pushEvent(
        'stock_in',
        command.business_id,
        line,
        line.source_qty * line.conversion.factor,
        line.stock_uom,
        line.conversion,
        null,
        {
          reason: command.reason ?? 'receive',
          lot_action: line.lot_action ?? 'updated_active',
        },
      );
    }
  } else if (command.movement_class === 'inventory_adjustment') {
    for (const line of command.lines) {
      const delta = line.counted_qty - line.system_qty;
      if (delta === 0) continue;
      const qty = Math.abs(delta);
      pushEvent(
        delta > 0 ? 'stock_in' : 'stock_out',
        command.business_id,
        {
          ...line,
          source_qty: qty,
          source_uom: line.uom,
          source_unit_price: null,
          source_line_total: null,
        },
        qty,
        line.uom,
        { factor: 1, source: 'identity' },
        line.unit_cost,
        {
          reason: command.reason ?? 'inventory_adjustment',
          inventory_system_qty: line.system_qty,
          inventory_counted_qty: line.counted_qty,
          inventory_delta: delta,
          inventory_counted_at: command.counted_at,
          lot_action: 'recorded_only',
        },
      );
    }
  } else if (command.movement_class === 'lot_state') {
    for (const line of command.lines) {
      const kind = line.action === 'close' ? 'stock_out' : 'stock_in';
      pushEvent(
        kind,
        command.business_id,
        {
          ...line,
          source_qty: 0,
          source_uom: line.uom,
          source_unit_price: null,
          source_line_total: null,
          currency: 'EUR',
        },
        0,
        line.uom,
        { factor: 1, source: 'identity' },
        null,
        {
          reason: command.reason ?? (
            line.action === 'close' ? 'lot_closed' : 'lot_state_updated'
          ),
          lot_state_action: line.action,
          ...(line.action === 'close'
            ? { lot_closed: true }
            : line.action === 'register'
              ? { lot_closed: false }
              : {}),
          // Metadata corrections update the historical lot balance without
          // silently replacing foods.current_* when another lot is active.
          lot_action: line.action === 'register'
            ? 'updated_active'
            : 'recorded_only',
          ...(line.source_production_id
            ? { source_production_id: line.source_production_id }
            : {}),
        },
      );
    }
  } else if (command.movement_class === 'correction') {
    for (const line of command.lines) {
      const source = line.source;
      const commonSource = {
        food_id: source.food_id,
        batch_lot: source.batch_lot,
        expiration_date: source.expiration_date,
        supplier_id: source.supplier_id,
        document_id: source.document_id,
        storage_unit_id: source.storage_unit_id,
        line_ref: line.line_ref,
        operator_name: line.operator_name,
        document_number: line.document_number,
        manual_entry: true,
        source_qty: source.qty,
        source_uom: source.uom,
        source_unit_price: null,
        source_line_total: null,
        currency: source.currency,
      };
      const correctionMetadata = {
        reason: 'lot_correction',
        correction_note: command.reason,
        correction_root_event_id: source.correction_root_event_id,
        corrects_event_id: source.original_event_id,
        ...(source.original_command_id
          ? {
              corrects_command_id: source.original_command_id,
              corrects_command_seq: source.original_command_seq,
            }
          : {}),
        expected_balance_qty: source.expected_balance.qty,
        expected_balance_reserved_qty: source.expected_balance.reserved_qty,
        expected_balance_uom: source.expected_balance.uom,
        expected_balance_event_count: source.expected_balance.event_count,
        expected_balance_last_event_ref: source.expected_balance.last_event_ref,
        expected_balance_last_server_rev: source.expected_balance.last_server_rev,
      };

      pushEvent(
        'stock_out',
        source.business_id,
        commonSource,
        source.qty,
        source.uom,
        { factor: 1, source: 'identity' },
        source.unit_cost,
        {
          ...correctionMetadata,
          correction_leg: 'source',
          corrected_to_qty: line.target.qty,
          corrected_to_uom: line.target.uom,
          corrected_to_lot: line.target.batch_lot,
          corrected_to_expiration_date: line.target.expiration_date,
          lot_action: 'recorded_only',
        },
      );

      const targetUnitCost = source.unit_cost == null
        ? null
        : (source.unit_cost * source.qty) / line.target.qty;
      pushEvent(
        'stock_in',
        source.business_id,
        {
          ...commonSource,
          batch_lot: line.target.batch_lot,
          expiration_date: line.target.expiration_date,
        },
        line.target.qty,
        line.target.uom,
        line.conversion,
        targetUnitCost,
        {
          ...correctionMetadata,
          correction_leg: 'target',
          corrected_from_qty: source.qty,
          corrected_from_uom: source.uom,
          corrected_from_lot: source.batch_lot,
          corrected_from_expiration_date: source.expiration_date,
          lot_action: 'updated_active',
        },
      );
    }
  } else if (command.movement_class === 'dispatch') {
    for (const line of command.lines) {
      pushEvent(
        'stock_out',
        command.business_id,
        line,
        line.source_qty * line.conversion.factor,
        line.stock_uom,
        line.conversion,
        null,
        {
          reason: command.reason ?? 'shipment_dispatch',
          source_shipment_id: command.shipment_id,
          lot_action: 'recorded_only',
        },
      );
    }
  } else if (command.movement_class === 'consume') {
    for (const line of command.lines) {
      pushEvent(
        'stock_out',
        command.business_id,
        line,
        line.source_qty * line.conversion.factor,
        line.stock_uom,
        line.conversion,
        null,
        {
          reason: command.reason,
          lot_action: 'recorded_only',
          ...(line.close_lot
            ? {
                lot_closed: true,
                lot_state_action: 'close',
              }
            : {}),
        },
      );
    }
  } else if (command.movement_class === 'uom_rebase') {
    const first = command.lines[0];
    if (first && 'zero_balance' in first) {
      pushEvent(
        'stock_in',
        command.business_id,
        first,
        0,
        first.stock_uom,
        first.conversion,
        null,
        {
          reason: command.reason ?? 'uom_rebase',
          corrected_from_uom: first.source_uom,
          uom_rebase_snapshot: true,
          uom_rebase_zero: true,
          lot_action: 'recorded_only',
        },
      );
      return events;
    }
    // Il cutover deve essere un confine unico: tutte le gambe nella vecchia
    // UdM precedono tutti gli snapshot nella nuova UdM. Il projector e i guard
    // DB possono così ignorare in modo deterministico l'intera storia
    // pre-rebase, inclusi gli stock_out tecnici del command.
    for (const line of command.lines) {
      pushEvent(
        'stock_out',
        command.business_id,
        line,
        line.source_qty,
        line.source_uom,
        { factor: 1, source: 'identity' },
        null,
        {
          reason: command.reason ?? 'uom_rebase',
          rebase_target_qty: line.source_qty * line.conversion.factor,
          rebase_target_uom: line.stock_uom,
          uom_rebase_source: true,
          lot_action: 'recorded_only',
        },
      );
    }
    for (const line of command.lines) {
      pushEvent(
        'stock_in',
        command.business_id,
        line,
        line.source_qty * line.conversion.factor,
        line.stock_uom,
        line.conversion,
        null,
        {
          reason: command.reason ?? 'uom_rebase',
          corrected_from_uom: line.source_uom,
          uom_rebase_snapshot: true,
          lot_action: 'recorded_only',
        },
      );
    }
  } else if (command.movement_class === 'transfer') {
    for (const line of command.lines) {
      const transferMetadata = {
        reason: command.reason ?? 'transfer',
        transfer_from_business_id: command.from_business_id,
        transfer_to_business_id: command.to_business_id,
        lot_action: 'recorded_only',
        ...(command.shipment_id ? { source_shipment_id: command.shipment_id } : {}),
      };
      pushEvent(
        'stock_out',
        command.from_business_id,
        line,
        line.source_qty,
        line.source_uom,
        { factor: 1, source: 'identity' },
        null,
        transferMetadata,
      );
      pushEvent(
        'stock_in',
        command.to_business_id,
        line,
        line.source_qty * line.conversion.factor,
        line.stock_uom,
        line.conversion,
        null,
        transferMetadata,
      );
    }
  } else if (command.movement_class === 'production') {
    for (const line of command.inputs) {
      pushEvent(
        'stock_out',
        command.business_id,
        line,
        line.source_qty * line.conversion.factor,
        line.stock_uom,
        line.conversion,
        null,
        {
          reason: command.reason ?? 'production_consumption',
          ...(command.production_id
            ? { source_production_id: command.production_id }
            : {}),
          lot_action: 'recorded_only',
        },
      );
    }
    for (const line of command.outputs) {
      pushEvent(
        'stock_in',
        command.business_id,
        line,
        line.source_qty * line.conversion.factor,
        line.stock_uom,
        line.conversion,
        null,
        {
          reason: command.reason ?? 'production_output',
          ...(command.production_id
            ? { source_production_id: command.production_id }
            : {}),
          finished_good: true,
          lot_action: line.lot_action ?? 'updated_active',
        },
      );
    }
  } else {
    const movements = [...command.movements].sort((left, right) => (
      right.original_seq - left.original_seq
    ));
    for (const movement of movements) {
      pushEvent(
        movement.kind === 'stock_in' ? 'stock_out' : 'stock_in',
        movement.business_id,
        movement,
        movement.stock_qty,
        movement.stock_uom,
        movement.conversion,
        movement.unit_cost,
        {
          reason: command.reason,
          void_compensation: true,
          reverses_command_id: command.reverses_command_id,
          reverses_command_seq: movement.original_seq,
          lot_action: 'recorded_only',
          reverses_event_id: movement.original_event_id,
          reverses_event_client_uuid:
            movement.original_event_client_uuid,
        },
      );
    }
  }

  return events;
}
